import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_STRING as api } from '@/App';
import { toast } from 'sonner';
import { useDarkMode } from '@/hooks/useDarkMode';
import { proxiedImage } from '@/utils/proxiedImage';

import { logger } from '@/utils/logger';
import { ArrowLeft, Check, ExternalLink, Heart, ShoppingBag, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import ProductModal from '@/components/ProductModal';
export default function KeepsakeResults({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const darkMode = useDarkMode();
  const [questionnaire, setQuestionnaire] = useState(null);
  const [recipient, setRecipient] = useState(null);
  const [savedProductIds, setSavedProductIds] = useState(new Set());
  const [savingProductId, setSavingProductId] = useState(null);
  const [previewProduct, setPreviewProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchResults = useCallback(async () => {
    try {
      const response = await fetch(`${api}/keepsakes/${id}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (!data.ai_recommendations || data.ai_recommendations.length === 0) {
          toast.error("This questionnaire hasn't been analyzed yet");
          navigate(`/keepsakes/${id}/questionnaire`);
          return;
        }
        setQuestionnaire(data);

        // Look up the recipient by name so we can save gifts to their
        // keepsake straight from this page.
        const rRes = await fetch(`${api}/recipients`, { credentials: 'include' });
        if (rRes.ok) {
          const list = await rRes.json();
          const match = list.find((r) => r.name === data.recipient_name);
          if (match) {
            setRecipient(match);
            // Hydrate the "already saved" set so Save buttons render in
            // the correct state on initial paint.
            const gRes = await fetch(
              `${api}/recipients/${match.id}/saved-gifts`,
              { credentials: 'include' }
            );
            if (gRes.ok) {
              const gifts = await gRes.json();
              setSavedProductIds(new Set(gifts.map((g) => g.saved_gift?.product_id || g.product_id).filter(Boolean)));
            }
          }
        }
      } else {
        toast.error('Results not available yet');
      }
    } catch (error) {
      logger.error('Failed to fetch results:', error);
      toast.error('Failed to load results');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchResults();
  }, [user, id, navigate, fetchResults]);

  const handleSaveToKeepsake = async (product) => {
    if (!recipient) {
      toast.error("Couldn't find the recipient profile");
      return;
    }
    if (savedProductIds.has(product.id)) return;
    setSavingProductId(product.id);
    try {
      const res = await fetch(`${api}/recipients/${recipient.id}/saved-gifts`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: product.id,
          // Tag the provenance so the recipient's keepsake card can show a
          // subtle "from questionnaire" indicator on this product.
          source: 'questionnaire'
        })
      });
      if (!res.ok) throw new Error('Save failed');
      setSavedProductIds((prev) => new Set(prev).add(product.id));
      toast.success(`Saved to ${recipient.name}'s keepsake`);
    } catch (err) {
      logger.error(err);
      toast.error('Failed to save gift');
    } finally {
      setSavingProductId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#9E6752]"></div>
      </div>
    );
  }

  if (!questionnaire) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl mb-4" style={{ color: '#73766A' }}>Results not found</p>
          <Link to="/keepsakes">
            <Button>Back to Keepsakes</Button>
          </Link>
        </div>
      </div>
    );
  }

  const confidenceBadge = (confidence) => {
    const styles = {
      high: { bg: '#D1FAE5', color: '#065F46', label: 'High Match' },
      medium: { bg: '#FEF3C7', color: '#92400E', label: 'Good Match' },
      low: { bg: '#DBEAFE', color: '#1E40AF', label: 'Possible Match' }
    };
    const style = styles[confidence] || styles.medium;
    return (
      <span
        className="px-3 py-1 rounded-full text-xs font-medium"
        style={{ background: style.bg, color: style.color }}
      >
        {style.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen page-enter" style={{ background: darkMode.bgPrimary }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back link returns to the recipient's profile so the workflow
            loops back where it started, not the Keepsakes index. */}
        <button
          onClick={() => navigate(`/keepsakes?recipientName=${encodeURIComponent(questionnaire.recipient_name)}`)}
          className="flex items-center gap-2 mb-6 text-sm hover:underline"
          style={{ color: '#9E6752' }}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to {questionnaire.recipient_name}
        </button>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <Sparkles className="w-8 h-8" style={{ color: '#9E6752' }} />
            <h1 className="text-3xl font-bold" style={{ color: '#20212B' }}>Gift Recommendations</h1>
          </div>
          <p className="text-lg" style={{ color: '#73766A' }}>for {questionnaire.recipient_name}'s {questionnaire.occasion}</p>
        </div>

        {/* Recipient Insight */}
        {questionnaire.ai_analysis && (
          <div className="bg-gradient-to-r from-[#FED7A5]/20 to-[#9E6752]/20 rounded-xl p-6 mb-8" style={{ border: '2px solid #FED7A5' }}>
            <h2 className="text-lg font-bold mb-3 flex items-center gap-2" style={{ color: '#20212B' }}>
              <Sparkles className="w-5 h-5" style={{ color: '#9E6752' }} />
              Recipient Insight
            </h2>
            <p className="text-base leading-relaxed" style={{ color: '#534145' }}>
              {questionnaire.ai_analysis}
            </p>
          </div>
        )}

        {/* Recommendations, 2-up on desktop, compact horizontal cards so
            the product image and reasoning sit side-by-side without leaving
            a mountain of dead space on the right at wide viewports. */}
        <div>
          <h2 className="text-2xl font-bold mb-6" style={{ color: '#20212B' }}>
            Recommended Gifts ({questionnaire.ai_recommendations.length})
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {questionnaire.ai_recommendations.map((rec, idx) => (
              <div
                key={`rec-${rec.product?.id || idx}`}
                className="bg-white rounded-xl overflow-hidden flex"
                style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}
              >
                {/* Product image (fixed slim column so the card stays compact) */}
                <Link
                  to={`/browse?product=${rec.product.id}`}
                  className="block flex-shrink-0 w-32 sm:w-40 relative overflow-hidden"
                  style={{ background: '#F3F4F6' }}
                >
                  {rec.product.image_url && (
                    <img
                      src={proxiedImage(rec.product.image_url)}
                      alt={rec.product.name}
                      className="absolute inset-0 w-full h-full object-cover"
                      loading="lazy"
                    />
                  )}
                </Link>

                {/* Reasoning + actions */}
                <div className="flex-1 p-4 sm:p-5 flex flex-col justify-between min-w-0">
                  <div>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="text-base sm:text-lg font-bold leading-tight" style={{ color: '#20212B', fontFamily: 'Playfair Display, serif' }}>
                        {rec.product.name}
                      </h3>
                      {confidenceBadge(rec.confidence)}
                    </div>
                    <p className="text-sm leading-relaxed mb-3 line-clamp-5" style={{ color: '#534145' }}>
                      {rec.reason}
                    </p>
                    <div className="flex items-center gap-3 text-sm mb-3">
                      <span style={{ color: '#73766A' }}>{rec.product.category}</span>
                      <span className="font-bold" style={{ color: '#9E6752' }}>${rec.product.price}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleSaveToKeepsake(rec.product)}
                      disabled={savingProductId === rec.product.id || savedProductIds.has(rec.product.id)}
                      className="flex-1 min-w-[120px]"
                      style={{
                        background: savedProductIds.has(rec.product.id) ? '#10B981' : '#9E6752',
                        color: 'white'
                      }}
                      data-testid={`save-to-keepsake-${rec.product.id}`}
                    >
                      {savedProductIds.has(rec.product.id) ? (
                        <>
                          <Check className="w-4 h-4 mr-1.5" />
                          Saved
                        </>
                      ) : savingProductId === rec.product.id ? (
                        'Saving…'
                      ) : (
                        <>
                          <Heart className="w-4 h-4 mr-1.5" />
                          Save to {recipient?.name || 'Keepsake'}
                        </>
                      )}
                    </Button>
                    {rec.product.merchant_link && (
                      <a
                        href={rec.product.merchant_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 min-w-[100px]"
                      >
                        <Button size="sm" className="w-full" style={{ background: '#2D4354', color: 'white' }}>
                          <ShoppingBag className="w-4 h-4 mr-1.5" />
                          Buy
                        </Button>
                      </a>
                    )}
                    <Link to={`/browse?product=${rec.product.id}`} className="flex-1 min-w-[100px]">
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full"
                        onClick={(e) => {
                          // Open the full product card in-place instead of
                          // bouncing to /browse and losing the results context.
                          e.preventDefault();
                          setPreviewProduct(rec.product);
                        }}
                        style={{ borderColor: '#9E6752', color: '#9E6752', background: 'white' }}
                        data-testid={`details-btn-${rec.product.id}`}
                      >
                        <ExternalLink className="w-4 h-4 mr-1.5" />
                        Details
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="mt-8 flex gap-3">
          <Link to="/browse" className="flex-1">
            <Button
              variant="outline"
              className="w-full"
              style={{ borderColor: '#9E6752', color: '#9E6752', background: 'white' }}
            >
              Browse More Gifts
            </Button>
          </Link>
          <Link to={recipient ? `/keepsakes?recipientName=${encodeURIComponent(recipient.name)}` : '/keepsakes'} className="flex-1">
            <Button className="w-full" style={{ background: '#9E6752', color: 'white' }}>
              {recipient ? `Back to ${recipient.name}` : 'Back to Keepsakes'}
            </Button>
          </Link>
        </div>
      </div>

      {/* In-place product detail, opens when "Details" is clicked on a
          recommendation card so the user never loses the results context. */}
      {previewProduct && (
        <ProductModal
          product={previewProduct}
          onClose={() => setPreviewProduct(null)}
          user={user}
        />
      )}
    </div>
  );
}
